<template>
  <button
    @click="$emit('click', $event)"
    :disabled="loading"
    :aria-disabled="loading"
    :aria-busy="loading"
    :class="[
      'auth-button',
      {
        'auth-button-disabled': showDisabledStyle,
        'auth-button-loading': loading,
      },
      className,
    ]"
  >
    <div class="button-content">
      <div v-if="loading" class="loading-spinner" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle
            class="spinner-circle"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="spinner-path"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
      <span v-if="loading">{{ loadingText }}</span>
      <span v-else>
        <slot>{{ text }}</slot>
      </span>
    </div>
  </button>
</template>

<script setup>
import { defineProps, defineEmits } from "vue";

defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  loadingText: {
    type: String,
    default: "Loading...",
  },
  showDisabledStyle: {
    type: Boolean,
    default: false,
  },
  text: {
    type: String,
    default: "",
  },
  className: {
    type: String,
    default: "",
  },
});

defineEmits(["click"]);
</script>

<style scoped>
.auth-button {
  border-radius: 0.75rem;
  cursor: pointer;
  font-size: var(--fs-big);
  font-weight: 700;
  transition: all 0.3s;
  height: 3rem;
  border: none;
  outline: none;
  background-color: var(--primary-blue, #003d6a);
  color: white;
}

.auth-button:active {
  transform: scale(0.95);
}

.auth-button:not(.auth-button-disabled):not(:disabled):hover {
  background-color: var(--blue-dark-2, #061f32);
  color: white;
}

.auth-button:disabled,
.auth-button-disabled {
  background-color: var(--neutral-100) !important;
  color: var(--neutral-700);
  cursor: not-allowed;
}

.auth-button:disabled:hover,
.auth-button-disabled:hover {
  background-color: var(--neutral-100) !important;
  color: var(--neutral-700);
}

.auth-button .button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 100%;
  font-size: var(--fs-big);
  padding: 0 1rem;
}

.auth-button .loading-spinner {
  animation: spin 1s linear infinite;
  height: 1.25rem;
  width: 1.25rem;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.auth-button .spinner-circle {
  opacity: 0.25;
}

.auth-button .spinner-path {
  opacity: 0.75;
}
</style>
